import { useState, useEffect } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const RULE_TYPES = [
  { value: "accepted_values", label: "Accepted Values" },
  { value: "unique", label: "Unique" },
];

const RULE_TYPE_DESCRIPTIONS = {
  accepted_values: "Restrict this column to a defined set of values.",
  unique: "Values in this column must be unique across all rows.",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DRAWER_WIDTH = 520;

const empty = () => ({
  column_name: "",
  check_type: "accepted_values",
  values: [],
});

/**
 * Props:
 *   open, onClose, onSave   — standard drawer controls
 *   columns                 — column objects from the template form (for column selector)
 *   initialData             — pre-populate when editing an existing check
 *   lockedColumnName        — when opened from a column link, locks the column
 *   saving                  — parent signals API call in progress (disables footer buttons)
 *
 * onSave receives: { _editingId, column_name, check_type, values, user_emails }
 * Parent is responsible for closing the drawer (call onClose from onSave on success).
 */
export default function ColumnCheckDrawer({
  open,
  onClose,
  onSave,
  columns = [],
  initialData,
  lockedColumnName,
  saving = false,
  saveError = "",
}) {
  const [form, setForm] = useState(empty());
  const [applyTo, setApplyTo] = useState("all"); // "all" | "specific"
  const [assignedEmails, setAssignedEmails] = useState([]);
  const [emailInputValue, setEmailInputValue] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      const base = initialData ? { ...empty(), ...initialData } : empty();
      if (lockedColumnName) base.column_name = lockedColumnName;
      setForm(base);
      const emails = initialData?.user_emails ?? [];
      setApplyTo(emails.length > 0 ? "specific" : "all");
      setAssignedEmails(emails);
      setEmailInputValue("");
      setErrors({});
    }
  }, [open, initialData, lockedColumnName]);

  const commitEmailInput = (raw) => {
    const email = raw.trim().toLowerCase();
    if (email && validateEmail(email) && !assignedEmails.includes(email)) {
      setAssignedEmails((prev) => [...prev, email]);
      setErrors((e) => { const { users: _, ...rest } = e; return rest; });
    }
    setEmailInputValue("");
  };

  const isColumnLocked = !!lockedColumnName;
  const columnNames = columns.map((c) => c.name);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleApplyToChange = (e) => {
    setApplyTo(e.target.value);
    if (e.target.value === "all") setAssignedEmails([]);
  };

  // Validates a candidate email before committing it to the chip list.
  const validateEmail = (email) => EMAIL_RE.test(email.trim().toLowerCase());

  const validate = () => {
    const errs = {};
    if (!form.column_name) errs.column_name = "Column is required.";
    if (!form.check_type) errs.check_type = "Rule type is required.";
    if (form.check_type === "accepted_values" && form.values.length === 0)
      errs.values = "Add at least one accepted value.";
    if (applyTo === "specific" && assignedEmails.length === 0)
      errs.users = 'Add at least one user email, or switch to "All users in this project".';
    return errs;
  };

  // "All users" maps to an empty array; the backend also accepts the zero UUID
  // "00000000-0000-0000-0000-000000000000" as an equivalent sentinel.
  const buildUserEmails = () => (applyTo === "all" ? [] : assignedEmails);

  // The parent controls when the drawer closes.
  const handleSave = () => {
    // Include any email still typed but not yet committed via Enter
    const pending = emailInputValue.trim().toLowerCase();
    const finalEmails = pending && validateEmail(pending) && !assignedEmails.includes(pending)
      ? [...assignedEmails, pending]
      : assignedEmails;

    const errs = validate();
    if (applyTo === "specific" && finalEmails.length === 0) {
      errs.users = 'Add at least one user email, or switch to "All users in this project".';
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }

    if (pending) setEmailInputValue("");
    onSave({
      _editingId: initialData?._id,
      column_name: form.column_name,
      check_type: form.check_type,
      values: form.check_type === "accepted_values" ? form.values : [],
      user_emails: applyTo === "all" ? [] : finalEmails,
    });
  };

  const drawerTitle = initialData
    ? "Edit Validation Rule"
    : lockedColumnName
    ? `New Rule — ${lockedColumnName}`
    : "New Validation Rule";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: DRAWER_WIDTH, p: 0, display: "flex", flexDirection: "column" } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h6">{drawerTitle}</Typography>
          {isColumnLocked && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Column:</Typography>
              <Chip label={lockedColumnName} size="small" color="primary" />
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 3 }}>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
        <Stack spacing={3}>
          {/* Column — locked to chip display when opened from a column link */}
          {!isColumnLocked && (
            <FormControl fullWidth required error={!!errors.column_name}>
              <InputLabel>Column</InputLabel>
              <Select value={form.column_name} onChange={set("column_name")} label="Column">
                {columnNames.map((col) => (
                  <MenuItem key={col} value={col}>{col}</MenuItem>
                ))}
              </Select>
              {errors.column_name && <FormHelperText>{errors.column_name}</FormHelperText>}
            </FormControl>
          )}

          {/* Rule Type */}
          <FormControl fullWidth required error={!!errors.check_type}>
            <InputLabel>Rule Type</InputLabel>
            <Select value={form.check_type} onChange={set("check_type")} label="Rule Type">
              {RULE_TYPES.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
            {errors.check_type && <FormHelperText>{errors.check_type}</FormHelperText>}
            {form.check_type && (
              <FormHelperText>{RULE_TYPE_DESCRIPTIONS[form.check_type]}</FormHelperText>
            )}
          </FormControl>

          {/* Accepted Values */}
          {form.check_type === "accepted_values" && (
            <Box>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={form.values}
                onChange={(_, newValues) => {
                  setForm((f) => ({ ...f, values: newValues }));
                  if (newValues.length > 0) setErrors((e) => { const { values: _, ...rest } = e; return rest; });
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, color, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option}
                        size="small"
                        {...tagProps}
                        sx={{
                          bgcolor: "#e0e0e0 !important",
                          color: "rgba(0, 0, 0, 0.87) !important",
                          "& .MuiChip-deleteIcon": { color: "rgba(0, 0, 0, 0.6) !important" },
                        }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Accepted Values"
                    placeholder={form.values.length === 0 ? "Type a value and press Enter…" : "Add another…"}
                    helperText={errors.values ?? "Values users are permitted to upload for this column."}
                    error={!!errors.values}
                  />
                )}
              />
            </Box>
          )}

          <Divider />

          {/* Apply To */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Apply To
            </Typography>
            <RadioGroup value={applyTo} onChange={handleApplyToChange}>
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                label="All users in this project"
              />
              <FormControlLabel
                value="specific"
                control={<Radio size="small" />}
                label="Specific users"
              />
            </RadioGroup>

            {applyTo === "specific" && (
              <Box sx={{ mt: 1.5 }}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={assignedEmails}
                  inputValue={emailInputValue}
                  onInputChange={(_, v) => setEmailInputValue(v)}
                  onChange={(_, newValues) => {
                    // Handles chip deletion (x button)
                    setAssignedEmails(newValues.map((v) => v.trim().toLowerCase()));
                    if (newValues.length > 0) setErrors((e) => { const { users: _, ...rest } = e; return rest; });
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((email, index) => {
                      const { key, color, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={email}
                          size="small"
                          {...tagProps}
                          sx={{
                            bgcolor: "#e0e0e0 !important",
                            color: "rgba(0, 0, 0, 0.87) !important",
                            "& .MuiChip-deleteIcon": { color: "rgba(0, 0, 0, 0.6) !important" },
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="User emails"
                      placeholder={assignedEmails.length === 0 ? "user@example.com — press Enter to add" : "Add another…"}
                      helperText={
                        errors.users ?? "Must belong to this project."
                      }
                      error={!!errors.users}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === "," || e.key === "Tab") && emailInputValue.trim()) {
                          e.preventDefault();
                          commitEmailInput(emailInputValue);
                        }
                      }}
                      onBlur={() => {
                        if (emailInputValue.trim()) commitEmailInput(emailInputValue);
                      }}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          gap: 2,
          justifyContent: "flex-end",
          flexShrink: 0,
        }}
      >
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Save Rule"}
        </Button>
      </Box>
    </Drawer>
  );
}
